import React, { useEffect, useId, useRef, useState } from 'react';

import {
  addMonths,
  format,
  isSameDay,
  isValid,
  Locale,
  parse,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';

import { Popover } from '@base-ui/react/popover';
import { Button, cx, Input, Island, Typography } from '@bolteu/clay-react';
import { Calendar } from '@bolteu/kalep-react-icons';

import { CalendarGrid } from '../CalendarGrid';
import { TimeColumn } from './TimeColumn';

export interface DateTimeFieldProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  locale: Locale;
  minDateTime?: Date;
  maxDateTime?: Date;
  disabled?: boolean;
  required?: boolean;
  okLabel?: string;
  openPickerLabel?: string;
  placeholder?: string;
  previousMonthLabel?: string;
  nextMonthLabel?: string;
  className?: string;
  minuteStep?: number;
}

const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm';
const DATE_TIME_PLACEHOLDER = 'DD/MM/YYYY HH:MM';
const DEFAULT_MINUTE_STEP = 5;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function withDatePart(base: Date, datePart: Date): Date {
  return new Date(
    datePart.getFullYear(),
    datePart.getMonth(),
    datePart.getDate(),
    base.getHours(),
    base.getMinutes(),
  );
}

function withTime(base: Date, hours: number, minutes: number): Date {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes);
}

function getMaxSelectableDay(maxDateTime: Date | undefined): Date | null {
  return maxDateTime ? startOfDay(maxDateTime) : null;
}

function getMinSelectableDay(minDateTime: Date | undefined): Date | null {
  return minDateTime ? startOfDay(minDateTime) : null;
}

function getAnchor(value: Date | null, minDateTime: Date | undefined, now: Date): Date {
  return value ?? minDateTime ?? now;
}

function getViewMonth(anchor: Date, minDay: Date | null, maxDay: Date | null): Date {
  let month = anchor;
  if (maxDay && month > maxDay) {
    month = maxDay;
  }
  if (minDay && month < minDay) {
    month = minDay;
  }
  return startOfMonth(month);
}

function clampDateTime(date: Date, minDateTime?: Date, maxDateTime?: Date): Date {
  if (minDateTime && date < minDateTime) {
    return minDateTime;
  }
  if (maxDateTime && date > maxDateTime) {
    return maxDateTime;
  }
  return date;
}

export function DateTimeField({
  label,
  value,
  onChange,
  locale,
  minDateTime,
  maxDateTime,
  disabled,
  required,
  okLabel = 'OK',
  // MUI's picker icon button ships the same untranslated default accessible name.
  openPickerLabel = 'Choose date',
  placeholder = DATE_TIME_PLACEHOLDER,
  previousMonthLabel,
  nextMonthLabel,
  className,
  minuteStep = DEFAULT_MINUTE_STEP,
}: DateTimeFieldProps) {
  const labelId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);

  const minutes = Array.from({ length: 60 / minuteStep }, (_, index) => index * minuteStep);

  const minDay = getMinSelectableDay(minDateTime);
  const maxDay = getMaxSelectableDay(maxDateTime);

  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState<string>(() =>
    value ? format(value, DATE_TIME_FORMAT, { locale }) : '',
  );
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setInputText(value ? format(value, DATE_TIME_FORMAT, { locale }) : '');
    }
  }, [value, isEditing, locale]);

  const parseInputText = (text: string): Date | null => {
    const parsed = parse(text, DATE_TIME_FORMAT, new Date(), { locale });
    return isValid(parsed) ? parsed : null;
  };

  const clampToRange = (date: Date) => clampDateTime(date, minDateTime, maxDateTime);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setInputText(text);
    // Commit only fully-typed dates; partial text like "15/09/2026 13:0"
    // would otherwise parse as 13:00 and fire a misleading onChange.
    if (text.length === DATE_TIME_FORMAT.length) {
      const parsed = parseInputText(text);
      if (parsed) {
        onChange(clampToRange(parsed));
      }
    }
  };

  const handleInputFocus = () => setIsEditing(true);

  const handleInputBlur = () => {
    setIsEditing(false);
    const formattedValue = value ? format(value, DATE_TIME_FORMAT, { locale }) : '';

    if (inputText === '') {
      if (!required && value !== null) {
        onChange(null);
      }
      setInputText(required ? formattedValue : '');
      return;
    }

    const parsed = parseInputText(inputText);
    if (parsed) {
      const next = clampToRange(parsed);
      const formattedNext = format(next, DATE_TIME_FORMAT, { locale });
      if (formattedNext !== formattedValue) {
        onChange(next);
      }
      setInputText(formattedNext);
    } else {
      setInputText(formattedValue);
    }
  };

  const [draftValue, setDraftValue] = useState<Date>(() =>
    getAnchor(value, minDateTime, new Date()),
  );
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    getViewMonth(getAnchor(value, minDateTime, new Date()), minDay, maxDay),
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const anchor = getAnchor(value, minDateTime, new Date());
      setDraftValue(anchor);
      setViewMonth(getViewMonth(anchor, minDay, maxDay));
    }
    setIsOpen(open);
  };

  const handleDaySelect = (date: Date) => {
    const next = clampToRange(withDatePart(draftValue, date));
    setDraftValue(next);
    onChange(next);
  };

  const handleHourSelect = (hour: number) => {
    const next = clampToRange(withTime(draftValue, hour, draftValue.getMinutes()));
    setDraftValue(next);
    onChange(next);
  };

  const handleMinuteSelect = (minute: number) => {
    const next = clampToRange(withTime(draftValue, draftValue.getHours(), minute));
    setDraftValue(next);
    onChange(next);
  };

  const handleOkClick = () => setIsOpen(false);

  const getDayState = (date: Date) => {
    const isSelected = isSameDay(date, draftValue);
    const isDisabled = Boolean((maxDay && date > maxDay) || (minDay && date < minDay));
    return { isStart: isSelected, isEnd: isSelected, isDisabled };
  };

  const onPrevMonth = () => setViewMonth((month) => subMonths(month, 1));
  const onNextMonth = () => setViewMonth((month) => addMonths(month, 1));

  const isPrevMonthDisabled = Boolean(minDay && startOfMonth(viewMonth) <= startOfMonth(minDay));
  const isNextMonthDisabled = Boolean(maxDay && startOfMonth(viewMonth) >= startOfMonth(maxDay));

  const selectedDay = startOfDay(draftValue);
  const isMinBoundaryDay = Boolean(minDay && isSameDay(selectedDay, minDay));
  const isMaxBoundaryDay = Boolean(maxDay && isSameDay(selectedDay, maxDay));
  const selectedHour = draftValue.getHours();
  // Exact match only: a value like 12:03 highlights no minute cell (the column offers
  // 5-minute steps, same as the legacy MUI picker) but still scrolls near it.
  const selectedMinute = draftValue.getMinutes();
  const nearestMinuteStep = Math.floor(selectedMinute / minuteStep) * minuteStep;

  const isHourDisabled = (hour: number) => {
    if (isMinBoundaryDay && minDateTime && hour < minDateTime.getHours()) {
      return true;
    }
    if (isMaxBoundaryDay && maxDateTime && hour > maxDateTime.getHours()) {
      return true;
    }
    return false;
  };

  const isMinuteDisabled = (minute: number) => {
    if (
      isMinBoundaryDay &&
      minDateTime &&
      selectedHour === minDateTime.getHours() &&
      minute < minDateTime.getMinutes()
    ) {
      return true;
    }
    if (
      isMaxBoundaryDay &&
      maxDateTime &&
      selectedHour === maxDateTime.getHours() &&
      minute > maxDateTime.getMinutes()
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className={className}>
      <Typography
        id={labelId}
        defaultTag="span"
        className="bolt-font-body-s-accent mb-2 block text-primary"
      >
        {label}
        {required ? (
          <span aria-hidden className="text-danger-primary">
            {' *'}
          </span>
        ) : null}
      </Typography>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <div ref={anchorRef}>
          <Input
            value={inputText}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-labelledby={labelId}
          >
            <Input.Control />
            <Input.Slot
              render={
                <Popover.Trigger
                  disabled={disabled}
                  aria-label={openPickerLabel}
                  className="cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed"
                />
              }
            >
              <Calendar size="sm" />
            </Input.Slot>
          </Input>
        </div>

        <Popover.Portal>
          <Popover.Positioner
            anchor={anchorRef}
            side="bottom"
            align="start"
            sideOffset={12}
            collisionPadding={8}
          >
            <Popover.Popup
              className={cx(
                'origin-[var(--transform-origin)]',
                'transition-[opacity,transform,translate,scale]',
                'duration-150 ease-out',
                'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
                'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              )}
            >
              <Island
                elevation="floor-2"
                className={cx(
                  'z-20 w-max max-h-[var(--available-height)] overflow-y-auto',
                  'px-0 pt-2 pb-0',
                )}
              >
                {/* The calendar drives the popup height; the time columns are pinned to it
                    absolutely (with reserved width) so they cap and scroll internally. */}
                <div className="relative flex items-stretch pr-[156px]">
                  <div className="flex-none">
                    <CalendarGrid
                      viewMonth={viewMonth}
                      locale={locale}
                      headerClassName="px-6 py-2"
                      isPrevMonthDisabled={isPrevMonthDisabled}
                      isNextMonthDisabled={isNextMonthDisabled}
                      onPrevMonth={onPrevMonth}
                      onNextMonth={onNextMonth}
                      getDayState={getDayState}
                      onDaySelect={handleDaySelect}
                      previousMonthLabel={previousMonthLabel}
                      nextMonthLabel={nextMonthLabel}
                    />
                  </div>
                  <div className="absolute inset-y-2 right-4 flex gap-1">
                    <TimeColumn
                      values={HOURS}
                      selectedValue={selectedHour}
                      isValueDisabled={isHourDisabled}
                      onSelect={handleHourSelect}
                      formatValue={pad}
                      ariaLabel="Hours"
                      isOpen={isOpen}
                    />
                    <TimeColumn
                      values={minutes}
                      selectedValue={selectedMinute}
                      scrollToValue={nearestMinuteStep}
                      isValueDisabled={isMinuteDisabled}
                      onSelect={handleMinuteSelect}
                      formatValue={pad}
                      ariaLabel="Minutes"
                      isOpen={isOpen}
                    />
                  </div>
                </div>
                <div className="flex justify-end px-4 py-3">
                  <Button variant="tertiary" size="sm" onClick={handleOkClick}>
                    {okLabel}
                  </Button>
                </div>
              </Island>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
